import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication, selectAvailableApplications } from '../../store/slices/application.slice';
import { hostApi } from '../../services/api/host.api';
import MainLayout from '../../components/layout/MainLayout';

interface AgentCard {
  id: string;
  name: string;
  description: string;
  icon: string;
  status: 'not_installed' | 'installed' | 'connected';
  installPath: string;
  connectedCount?: number;
}

export default function AgentsPage() {
  const { t } = useTranslation('agents');
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const availableApps = useAppSelector(selectAvailableApplications);
  const [agents, setAgents] = useState<AgentCard[]>([]);
  const [loading, setLoading] = useState(true);

  // Get selected application name
  const selectedApp = availableApps.find(app => app.id === selectedAppId);
  const isAllAppsSelected = !selectedAppId || selectedAppId === 'all';

  useEffect(() => {
    loadAgentStatus();
  }, []);

  const loadAgentStatus = async () => {
    try {
      // Check host agent status
      const hostsResponse = await hostApi.getHosts();
      const connectedHosts = hostsResponse.data ? hostsResponse.data.filter((h: any) => h.status === 'online').length : 0;

      // For now, we don't show installation status for agents we can't verify
      // Only show 'connected' status when we can actually verify the agent is running
      setAgents([
        {
          id: 'host',
          name: t('agents.host.name'),
          description: t('agents.host.description'),
          icon: '🖥️',
          status: connectedHosts > 0 ? 'connected' : 'not_installed',
          installPath: '/agents/host/install',
          connectedCount: connectedHosts,
        },
        {
          id: 'kubernetes',
          name: t('agents.kubernetes.name'),
          description: t('agents.kubernetes.description'),
          icon: '☸️',
          status: 'not_installed',
          installPath: '/agents/kubernetes/install',
          connectedCount: 0,
        },
        {
          id: 'logs',
          name: t('agents.logs.name'),
          description: t('agents.logs.description'),
          icon: '📝',
          status: 'not_installed',
          installPath: '/agents/logs/install',
          connectedCount: 0,
        },
        {
          id: 'apm',
          name: t('agents.apm.name'),
          description: t('agents.apm.description'),
          icon: '📊',
          status: 'not_installed',
          installPath: '/agents/apm/install',
          connectedCount: 0,
        },
        {
          id: 'browser-sdk',
          name: t('agents.browserSdk.name'),
          description: t('agents.browserSdk.description'),
          icon: '🌐',
          status: 'not_installed',
          installPath: '/analytics/sdk-setup',
          connectedCount: 0,
        },
        {
          id: 'mobile-sdk',
          name: t('agents.mobileSdk.name'),
          description: t('agents.mobileSdk.description'),
          icon: '📱',
          status: 'not_installed',
          installPath: '/analytics/mobile/setup',
          connectedCount: 0,
        },
      ]);
    } catch (error) {
      console.error('Failed to load agent status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Agents that require a specific application (not "All Applications")
  const requiresSpecificApp = (agentId: string) => {
    return ['logs', 'apm', 'browser-sdk', 'mobile-sdk'].includes(agentId);
  };

  const handleAgentClick = (agent: AgentCard) => {
    // Check if agent requires specific application
    if (requiresSpecificApp(agent.id) && isAllAppsSelected) {
      toast.error(
        t('errors.selectApplication') || 'Please select a specific application to configure this agent.',
        {
          duration: 5000,
          icon: '⚠️',
        }
      );
      return;
    }

    navigate(agent.installPath);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-green-500 animate-pulse"></span>
            {t('status.connected')}
          </span>
        );
      case 'installed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-yellow-500"></span>
            {t('status.installed')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
            {t('status.notInstalled')}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('subtitle')}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('stats.totalAgents')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{agents.length}</p>
            </div>
            <div className="text-3xl">🔧</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('stats.connected')}</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {agents.filter(a => a.status === 'connected').length}
              </p>
            </div>
            <div className="text-3xl">✅</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('stats.installed')}</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {agents.filter(a => a.status === 'installed').length}
              </p>
            </div>
            <div className="text-3xl">⚙️</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('stats.notInstalled')}</p>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                {agents.filter(a => a.status === 'not_installed').length}
              </p>
            </div>
            <div className="text-3xl">📦</div>
          </div>
        </div>
      </div>

      {/* Selected Application Alert */}
      {isAllAppsSelected && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="text-2xl">⚠️</div>
            <div>
              <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-1">
                {t('warnings.applicationRequired.title') || 'Application Selection Required'}
              </h4>
              <p className="text-sm text-yellow-800 dark:text-yellow-400">
                {t('warnings.applicationRequired.description') || 'Some agents require a specific application to be selected. Please select an application from the top menu to configure APM, Logs, and Browser SDK agents.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Agent Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-tour="agents-tabs">
        {agents.map((agent) => {
          const isBlocked = requiresSpecificApp(agent.id) && isAllAppsSelected;

          return (
            <div
              key={agent.id}
              className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors ${
                isBlocked
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:border-indigo-300 dark:hover:border-indigo-700 cursor-pointer'
              }`}
              onClick={() => !isBlocked && handleAgentClick(agent)}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="text-4xl">{agent.icon}</div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        {agent.name}
                        {isBlocked && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded-full">
                            {t('badges.appRequired') || 'App Required'}
                          </span>
                        )}
                      </h3>
                      {agent.status === 'connected' && agent.connectedCount !== undefined && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {agent.connectedCount} {agent.connectedCount === 1 ? t('instances.one') : t('instances.other')} {t('status.connected').toLowerCase()}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Only show status badge for agents we can actually verify */}
                  {agent.id === 'host' && getStatusBadge(agent.status)}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {agent.description}
                </p>

                {/* Action Button */}
                <button
                  className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isBlocked
                      ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                      : 'text-white bg-indigo-600 hover:bg-indigo-700'
                  }`}
                  disabled={isBlocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isBlocked) {
                      handleAgentClick(agent);
                    }
                  }}
                >
                  {isBlocked
                    ? t('buttons.selectAppFirst') || 'Select Application First'
                    : agent.status === 'connected'
                      ? t('buttons.viewDetails')
                      : t('buttons.installNow')
                  }
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="text-2xl">💡</div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
              {t('gettingStarted.title')}
            </h4>
            <p className="text-sm text-blue-800 dark:text-blue-400">
              {t('gettingStarted.description')}
            </p>
          </div>
        </div>
      </div>
      </div>
    </MainLayout>
  );
}
